import { Injectable } from "@angular/core";
import * as Inspector from "avo-inspector";
import { AvoSystemProperties } from "../window-util/window-injection-token/window-injection-token";
import { WindowReferenceService } from "../window-util/window.reference/window.reference.service";
import Avo, {
  AddPaymentInfoProperties,
  AddShippingInfoProperties,
  AddToCartProperties,
  AvoEnv,
  BeginCheckoutProperties,
  ButtonClickProperties,
  FormErrorProperties,
  FormStartProperties,
  FormSubmitProperties,
  LinkClickProperties,
  NsEnvironment,
  NsEnvironmentValueType,
  OrderChangeProperties,
  PageViewProperties,
  PurchaseProperties,
  RemoveFromCartProperties,
  SelectableClickProperties,
  SelectItemProperties,
  ToggleClickProperties,
  ViewItemListProperties,
  ViewItemProperties,
  ClickProperties,
  SelectClickProperties,
  DatepickerClickProperties,
  CheckboxClickProperties,
  ExpandableClickProperties
} from "./Avo";
import { AvoApiKey, AvoConfiguration } from "./avo-configuration.model";
import { datepickerClick } from './Avo';


export interface AvoEvent {
  event: string;
  data_layer_source: "AVO";
  [any: string]: any;
}

@Injectable({
  providedIn: "root",
})
export class AvoService {
  public avoInstance = Avo;
  public avoInitialized = false;
  private isProdOrTestaut = false;
  private hasDataQueue = false;
  private systemProps!: AvoSystemProperties;
  private isE2e = false;

  public constructor(
    private readonly configuration: AvoConfiguration,
    private readonly windowRefService: WindowReferenceService,
  ) {
    this.initializeDataQueue();
    this.systemProps = this.windowRefService.nativeWindow
      .AVO_SYSTEM_PROPERTIES as AvoSystemProperties;
    this.isProdOrTestaut =
      this.systemProps.ns_environment === NsEnvironment.PROD ||
      this.systemProps.ns_environment === NsEnvironment.TESTAUT ||
      this.systemProps.ns_environment === NsEnvironment.TESTAUT2 ||
      this.systemProps.ns_environment === NsEnvironment.TESTAUT3;
    this.isE2e = this.systemProps.ns_environment === NsEnvironment.UNITTEST;
    this.initAvo();
  }

  public get instance(): typeof Avo {
    return this.avoInstance;
  }

  public static generatePageStructure(
    appName: string,
    pageName: string,
  ): string {
    return `ns.nl/businesscardaanvragen | Zakelijk | ${appName} | ${pageName}`;
  }

  public initAvo(): void {
    if (this.configuration && this.systemProps) {
      const inspector = new Inspector.AvoInspector({
        apiKey: AvoApiKey,
        // switch lines for verbose debugging
        // env: Inspector.AvoInspectorEnv.Dev,
        env: Inspector.AvoInspectorEnv.Staging,
        version: "1.0.0",
        appName: "My App",
      });

      this.avoInstance.initAvo(
        {
          env: this.isProdOrTestaut ? AvoEnv.Prod : AvoEnv.Dev,
          webDebugger: !this.isProdOrTestaut && !this.isE2e ? true : false,
        },
        {
          platform: "web",
          language: this.systemProps.language,
          nsEnvironment:
            this.systemProps.ns_environment.toLowerCase() as NsEnvironmentValueType,
          datalayerSource: "AVO",
        },
        null,
        {
          make: (_env: string, _apiKey: string) => this.make(_env, _apiKey),
          logEvent: (eventName: string, eventProps: Record<string, any>) =>
            this.logEvent(eventName, eventProps, inspector),
        },
      );
      this.avoInitialized = true;
    }
  }

  public invokeAvoMethod(method: string, properties: any): void {
    if (!this.avoInitialized) return;
    const methodToCall = (this.avoInstance as any)[method];
    if (typeof methodToCall === "function") {
      methodToCall.call(this.avoInstance, properties);
    } else {
      console.warn(`Avo method ${method} does not exist.`);
    }
  }

  public addPaymentInfo(properties: AddPaymentInfoProperties): void {
    this.invokeAvoMethod("addPaymentInfo", properties);
  }
  public addShippingInfo(properties: AddShippingInfoProperties): void {
    this.invokeAvoMethod("addShippingInfo", properties);
  }
  public addToCart(properties: AddToCartProperties): void {
    this.invokeAvoMethod("addToCart", properties);
  }
  public beginCheckout(properties: BeginCheckoutProperties): void {
    this.invokeAvoMethod("beginCheckout", properties);
  }
  public buttonClick(properties: ButtonClickProperties): void {
    this.invokeAvoMethod("buttonClick", properties);
  }
  public linkClick(properties: LinkClickProperties): void {
    this.invokeAvoMethod("linkClick", properties);
  }
  public pageView(properties: PageViewProperties): void {
    this.invokeAvoMethod("pageView", properties);
  }
  public purchase(properties: PurchaseProperties): void {
    this.invokeAvoMethod("purchase", properties);
  }
  public removeFromCart(properties: RemoveFromCartProperties): void {
    this.invokeAvoMethod("removeFromCart", properties);
  }
  public selectItem(properties: SelectItemProperties): void {
    this.invokeAvoMethod("selectItem", properties);
  }
  public viewItem(properties: ViewItemProperties): void {
    this.invokeAvoMethod("viewItem", properties);
  }
  public viewItemList(properties: ViewItemListProperties): void {
    this.invokeAvoMethod("viewItemList", properties);
  }
  public selectableClick(properties: SelectableClickProperties): void {
    this.invokeAvoMethod("selectableClick", properties);
  }
  public selectClick(properties: SelectClickProperties): void {
    this.invokeAvoMethod("selectClick", properties);
  }
  public click(properties: ClickProperties): void {
    this.invokeAvoMethod("click", properties);
  }
  public toggleClick(properties: ToggleClickProperties): void {
    this.invokeAvoMethod("toggleClick", properties);
  }
  public formStart(properties: FormStartProperties): void {
    this.invokeAvoMethod("formStart", properties);
  }
  public formSubmit(properties: FormSubmitProperties): void {
    this.invokeAvoMethod("formSubmit", properties);
  }
  public formError(properties: FormErrorProperties): void {
    this.invokeAvoMethod("formError", properties);
  }
  public orderChange(properties: OrderChangeProperties): void {
    this.invokeAvoMethod("orderChange", properties);
  }
  public expandableClick(properties: ExpandableClickProperties): void {
    this.invokeAvoMethod("expandableClick", properties);
  }
  public checkboxClick(properties: CheckboxClickProperties): void {
    this.invokeAvoMethod("checkboxClick", properties);
  }
  public datepickerClick(properties: DatepickerClickProperties): void {
    this.invokeAvoMethod("datepickerClick", properties);
  }

  private logEvent(
    eventName: string,
    eventProps: Record<string, any>,
    inspector: Inspector.AvoInspector,
  ): void {
    if (this.configuration && !this.isProdOrTestaut) {
      inspector.trackSchemaFromEvent(eventName, eventProps);
    }
    this.sendData({
      event: eventName,
      data_layer_source: "AVO",
      ...eventProps,
    });
  }

  private sendData(event: AvoEvent): void {
    if (this.hasDataQueue && this.avoInitialized) {
      this.windowRefService.nativeWindow.nsDataQueue.push(event);
    }
  }

  private make(env: string, apiKey: string): void {
    if (!this.isProdOrTestaut) {
      console.log("AVO initialized", env, apiKey);
    }
  }

  private initializeDataQueue(): void {
    this.hasDataQueue =
      typeof window !== "undefined" &&
      !!this.windowRefService.nativeWindow.nsDataQueue;
  }
}
